import { useState } from "react";
import Button from "../../../../components/Button/Button";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import { Section, Card } from "../Account/accountParts";
import { managerTypeLabel, applicationFormLink } from "./vendorPortalOptions";
import DefaultAgreementNotice from "../../../../components/Agreement/DefaultAgreementNotice";

const YesNoBadge = ({ value }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
      value ? "bg-success-100 text-success-800" : "bg-gray-100 text-gray-600"
    }`}
  >
    {value ? "Yes" : "No"}
  </span>
);

const ExternalLink = ({ href }) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline break-all">
      {href}
    </a>
  ) : null;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : null);

const SettingsTable = ({ rows }) => (
  <div className="-mx-5 overflow-x-auto">
    <table className="w-full min-w-[560px] table-auto border-collapse">
      <thead>
        <tr className="border-b bg-white text-[10px] uppercase tracking-widest text-gray-500">
          <th className="py-2 pl-5 pr-3 text-left font-semibold whitespace-nowrap">Setting</th>
          <th className="py-2 pl-3 pr-5 text-left font-semibold whitespace-nowrap">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, value], index) => (
          <tr key={label} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
            <td className="w-1/2 py-2.5 pl-5 pr-3 text-xs font-medium text-gray-900">{label}</td>
            <td className="py-2.5 pl-3 pr-5 text-xs text-gray-900">
              {value === null || value === undefined || value === "" ? "—" : value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VendorPortalView = ({
  settings,
  agreement,
  canEdit,
  onEditSettings,
  onEditAgreement,
  onDeleteAgreement,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    setDeleting(true);
    await onDeleteAgreement();
    setDeleting(false);
    setConfirmingDelete(false);
  };

  return (
    <div className="space-y-6">
      <Section
        title="Vendor Portal Settings"
        large
        actions={
          canEdit && (
            <Button size="sm" onClick={onEditSettings}>
              Update
            </Button>
          )
        }
      >
        <Card>
          <SettingsTable
            rows={[
              [
                "Default Payment Terms for Vendor Invoices",
                `${settings.default_payment_terms_days} days`,
              ],
              [
                "Allow Vendors to Create Invoices",
                <YesNoBadge value={settings.vendors_can_create_invoices} />,
              ],
              [
                "Recipient of Vendor Invoice Related Notifications",
                managerTypeLabel(settings.invoice_notification_recipient),
              ],
              [
                "Vendor Application Form Is Enabled",
                <YesNoBadge value={settings.application_form_enabled} />,
              ],
              [
                "Vendor Application Form Link",
                settings.application_form_token && (
                  <ExternalLink href={applicationFormLink(settings.application_form_token)} />
                ),
              ],
              [
                "Vendor Application Form URL",
                settings.application_form_url && (
                  <ExternalLink href={settings.application_form_url} />
                ),
              ],
              [
                "Recipient of Notifications About New Vendor Applications",
                managerTypeLabel(settings.application_notification_recipient),
              ],
            ]}
          />
        </Card>
      </Section>

      <Section
        title="Vendor Agreement"
        large
        actions={
          canEdit && (
            <>
              <Button size="sm" onClick={onEditAgreement}>
                Update
              </Button>
              {!agreement.is_default && (
                <Button size="sm" variant="danger" onClick={() => setConfirmingDelete(true)}>
                  Delete
                </Button>
              )}
            </>
          )
        }
      >
        <Card className="space-y-4">
          <p className="text-xs text-gray-500">
            Vendor agreement must be accepted by the vendors invited to Globilise during their
            registration process.
          </p>

          <SettingsTable
            rows={[
              ["URL", agreement.url && <ExternalLink href={agreement.url} />],
              ["Last Updated At", formatDateTime(agreement.updated_at)],
              [
                "Last Updated By",
                agreement.updated_by || (agreement.is_default ? "Default agreement" : null),
              ],
            ]}
          />

          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500">Vendor Agreement Text:</p>
            {agreement.is_default && <DefaultAgreementNotice className="mb-3" />}
            {agreement.text ? (
              <div
                className="prose prose-sm max-w-none break-words rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900"
                dangerouslySetInnerHTML={{ __html: agreement.text }}
              />
            ) : (
              <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                No vendor agreement has been set.
              </p>
            )}
          </div>
        </Card>
      </Section>

      {confirmingDelete && (
        <ConfirmModal
          title="Delete Vendor Agreement"
          message="The agreement will go back to the default one, and every vendor will be emailed and asked to accept it again. Are you sure you want to delete it?"
          onCancel={() => !deleting && setConfirmingDelete(false)}
          onConfirm={handleConfirmDelete}
          confirmText={deleting ? "Deleting..." : "Yes, Delete"}
        />
      )}
    </div>
  );
};

export default VendorPortalView;